import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressBook,
  faBuilding,
  faEnvelope,
  faPhone,
  faRotate,
  faSearch,
  faSpinner,
  faUserTie,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { getlist } from "../../contexts/apicontexts";
import config from "../../config";
import { useTheme } from "../../contexts/themContext";
import { getCurrentUserIdentity } from "../Tickets/ticketUtils";

const BRAND_LR   = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135  = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const PAGE_SIZE = 10;

const normalizeCustomer = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? ""),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? ""),
  email: String(item?.email ?? item?.Email ?? ""),
  mobile: String(item?.mobile ?? item?.Mobile ?? ""),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? ""),
  domain: String(item?.domain ?? item?.Domain ?? ""),
  address: String(item?.address ?? item?.Address ?? ""),
  isCurrentCustomer: Boolean(item?.isCurrentCustomer ?? item?.IsCurrentCustomer),
  agreementStartDate: item?.agreementStartDate ?? item?.AgreementStartDate ?? "",
  agreementEndDate: item?.agreementEndDate ?? item?.AgreementEndDate ?? "",
  empUserId: String(item?.empUserId ?? item?.EmpUserId ?? ""),
});

const formatDate = (value) => {
  if (!value) return "Not set";
  try {
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(new Date(value));
  } catch { return value; }
};

export default function MyCustomersPage() {
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const { isDarkMode } = useTheme();
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadCustomers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getlist(`${config.baseurl}Customer/getmylist`);
      if (response?.status === 200) {
        setCustomers(Array.isArray(response.data) ? response.data.map(normalizeCustomer) : []);
      } else {
        setError("Assigned customer list could not be loaded.");
      }
    } catch {
      setError("Assigned customer list could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCustomers(); }, []);
  useEffect(() => { setPage(1); }, [searchTerm]);

  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((item) =>
      [item.name, item.customerCode, item.email, item.mobile, item.organizationName, item.domain, item.address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [customers, searchTerm]);

  const paginatedCustomers = useMemo(
    () => filteredCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredCustomers, page]
  );

  const totalPages  = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const activeCount = customers.filter((c) => c.isCurrentCustomer).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-3 transition-colors duration-300 dark:from-slate-950 dark:via-[#0F111A] dark:to-slate-900 md:p-5">
      <Breadcrumb pageName="My Customers" currentPage="My Customers" />

      <div
        className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl transition-colors duration-300 dark:border-gray-800 dark:bg-[#161B22]"
        style={{ boxShadow: isDarkMode ? "0 16px 48px rgba(0,0,0,0.28)" : "0 16px 48px rgba(192,76,42,0.10)" }}
      >

        {/* ── Header ── */}
        <div
          className="relative overflow-hidden px-4 py-5 text-white sm:px-6"
          style={{ background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 55%, #020617 100%)" : BRAND_135 }}
        >
          <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-24 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* left: title + user */}
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
                <FontAwesomeIcon icon={faAddressBook} />
                My Customers
              </div>
              <h1 className="mt-2 text-lg font-extrabold tracking-tight text-white sm:text-xl">Assigned Customer List</h1>
              <p className="mt-0.5 text-[11px] text-orange-200">{identity.fullName || "User"} &nbsp;·&nbsp; {identity.email || "—"}</p>
            </div>

            {/* right: stat cards */}
            <div className="grid grid-cols-2 gap-2 sm:w-64">
              <HeroStat label="Assigned" value={customers.length} icon={faUserTie}     bg="rgba(255,255,255,0.13)" />
              <HeroStat label="Active"   value={activeCount}      icon={faCheckCircle} bg="rgba(52,211,153,0.22)"  />
            </div>
          </div>
        </div>

        {/* ── Search + Refresh ── */}
        <div className="flex flex-col gap-2 border-b border-orange-100 bg-gradient-to-r from-orange-50/60 to-red-50/40 px-4 py-3 transition-colors duration-300 dark:border-gray-800 dark:from-gray-900/80 dark:to-gray-800/50 sm:flex-row sm:items-center sm:px-6">
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C04C2A]/50 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, org, domain…"
              className="w-full rounded-xl border border-orange-100 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
              onBlur={(e)  => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
            />
          </div>
          <button
            type="button"
            onClick={loadCustomers}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-4 py-2 text-xs font-bold text-white transition hover:opacity-90"
            style={{ background: BRAND_LR, borderColor: "transparent" }}
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
            Refresh
          </button>
        </div>

        {/* ── Content ── */}
        <div className="p-4 transition-colors duration-300 dark:bg-[#161B22] sm:p-5">
          {error ? (
            <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">{error}</div>
          ) : null}

          {loading ? (
            <div
              className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 px-6 py-14 text-center text-sm dark:border-gray-700 dark:bg-gray-900/40"
              style={{ color: isDarkMode ? "#fb923c" : "#C04C2A" }}
            >
              <div className="mb-3 flex justify-center">
                <DotLoader color={isDarkMode ? "#fb923c" : "#C04C2A"} size="w-1.5 h-1.5" />
              </div>
              Loading assigned customers…
            </div>
          ) : paginatedCustomers.length ? (
            <>
              {/* Table */}
              <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm dark:border-gray-800">
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white text-sm dark:bg-[#161B22]">
                    <thead>
                      <tr
                        className="text-left text-[10px] font-bold uppercase tracking-widest text-white"
                        style={{ background: isDarkMode ? "linear-gradient(to right, #1f2937, #020617)" : BRAND_LR }}
                      >
                        <th className="px-4 py-3">#</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Organization</th>
                        <th className="px-4 py-3">Contact</th>
                        <th className="px-4 py-3">Agreement</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCustomers.map((item, index) => {
                        const initials = item.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
                        const rowNum = (page - 1) * PAGE_SIZE + index + 1;
                        return (
                          <tr
                            key={item.id}
                            className="border-b border-slate-100 transition-colors last:border-0 dark:border-gray-800"
                            style={{ background: isDarkMode ? (index % 2 === 0 ? "#161B22" : "rgba(30,41,59,0.55)") : (index % 2 === 0 ? "#fff" : "rgba(192,76,42,0.02)") }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = isDarkMode ? "rgba(51,65,85,0.72)" : "rgba(192,76,42,0.04)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = isDarkMode ? (index % 2 === 0 ? "#161B22" : "rgba(30,41,59,0.55)") : (index % 2 === 0 ? "#fff" : "rgba(192,76,42,0.02)"); }}
                          >
                            {/* # */}
                            <td className="px-4 py-3 align-middle">
                              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-[10px] font-bold text-slate-400 dark:bg-gray-800 dark:text-gray-400">
                                {rowNum}
                              </span>
                            </td>

                            {/* Customer */}
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-2.5">
                                <div
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm"
                                  style={{ background: BRAND_LR }}
                                >
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate font-semibold text-slate-800 dark:text-gray-100">{item.name || "Unnamed"}</div>
                                  <div className="text-[10px] text-slate-400 dark:text-gray-500">{item.customerCode || "—"}</div>
                                  {item.domain ? <div className="text-[10px] text-slate-400 dark:text-gray-500">{item.domain}</div> : null}
                                </div>
                              </div>
                            </td>

                            {/* Organization */}
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-1.5 text-[12px] text-slate-600 dark:text-gray-300">
                                <FontAwesomeIcon icon={faBuilding} className="shrink-0 text-[10px]" style={{ color: "#C04C2A" }} />
                                <span className="truncate max-w-[140px]">{item.organizationName || "—"}</span>
                              </div>
                              {item.address ? (
                                <div className="mt-0.5 line-clamp-1 max-w-[160px] text-[10px] text-slate-400 dark:text-gray-500">{item.address}</div>
                              ) : null}
                            </td>

                            {/* Contact */}
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-gray-300">
                                <FontAwesomeIcon icon={faEnvelope} className="shrink-0 text-[10px]" style={{ color: "#C04C2A" }} />
                                <span className="truncate max-w-[160px]">{item.email || "—"}</span>
                              </div>
                              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-gray-400">
                                <FontAwesomeIcon icon={faPhone} className="shrink-0 text-[10px]" style={{ color: "#C04C2A" }} />
                                <span>{item.mobile || "—"}</span>
                              </div>
                            </td>

                            {/* Agreement */}
                            <td className="px-4 py-3 align-middle">
                              <div className="text-[11px] text-slate-700 dark:text-gray-300">
                                <span className="font-medium" style={{ color: "#C04C2A" }}>Start:</span> {formatDate(item.agreementStartDate)}
                              </div>
                              <div className="mt-0.5 text-[11px] text-slate-500 dark:text-gray-400">
                                <span className="font-medium">End:</span> {formatDate(item.agreementEndDate)}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3 align-middle">
                              <span
                                className="inline-flex rounded-lg border px-2.5 py-0.5 text-[11px] font-bold"
                                style={
                                  item.isCurrentCustomer
                                    ? { background: isDarkMode ? "rgba(6,78,59,0.35)" : "#ecfdf5", borderColor: isDarkMode ? "rgba(52,211,153,0.45)" : "#6ee7b7", color: isDarkMode ? "#6ee7b7" : "#065f46" }
                                    : { background: isDarkMode ? "rgba(30,41,59,0.65)" : "#f8fafc", borderColor: isDarkMode ? "rgba(100,116,139,0.55)" : "#e2e8f0", color: "#94a3b8" }
                                }
                              >
                                {item.isCurrentCustomer ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {filteredCustomers.length > PAGE_SIZE ? (
                <div className="mt-4 flex flex-col gap-2 border-t border-orange-100 pt-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-slate-400 dark:text-gray-500">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredCustomers.length)} of {filteredCustomers.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-xl border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Prev
                    </button>
                    <span
                      className="rounded-xl border px-3 py-1.5 text-xs font-bold text-white"
                      style={{ background: BRAND_LR, borderColor: "transparent" }}
                    >
                      {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="rounded-xl border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div
              className="rounded-2xl border border-dashed px-6 py-14 text-center"
              style={{ borderColor: isDarkMode ? "rgba(100,116,139,0.48)" : "rgba(192,76,42,0.22)", background: isDarkMode ? "rgba(15,23,42,0.45)" : "rgba(192,76,42,0.03)" }}
            >
              <div
                className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-md"
                style={{ background: BRAND_LR }}
              >
                <FontAwesomeIcon icon={faAddressBook} />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">No assigned customers found</h3>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-gray-400">
                No customer record is currently mapped to your login via the{" "}
                <span className="font-semibold" style={{ color: "#C04C2A" }}>EmpUserId</span> field.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Header stat card (lives inside gradient) ── */
function HeroStat({ label, value, icon, bg }) {
  return (
    <div
      className="rounded-xl border border-white/15 px-3 py-2 backdrop-blur-sm"
      style={{ background: bg }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/20">
          <FontAwesomeIcon icon={icon} className="text-xs text-white" />
        </div>
        <div className="min-w-0 text-right">
          <div className="text-[9px] font-bold uppercase tracking-widest text-orange-100">{label}</div>
          <div className="text-xl font-extrabold leading-tight text-white">{value}</div>
        </div>
      </div>
    </div>
  );
}
